import {graneroInstance} from '@apps-common/geocorn-sdk/api';
import {
  deleteCollection,
  getCollection,
  getCollections,
  getCollectionPropertyNames,
  shareCollection,
  unshareCollection,
  updateCollection,
} from '@apps-common/geocorn-sdk/api/collections';
import {DATASETS} from '@apps-common/geocorn-sdk/api/datasets';
import {
  type Feature,
  type FeatureCollection,
  type FeatureCollectionId,
  type Link,
} from '@src/types/featureCollections.ts';
import {getHashFromId, hashStringSHA256} from '@src/utils/features.ts';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import {type AxiosResponse} from 'axios';
import {DURATION_MS, getQueryKeys} from './util.js';

export const queryKeys = getQueryKeys('featureCollections');

// -------------------------
// Fetch functions

// TODO: this should probably come from geocorn-sdk
const validateFeature = (feature: Feature) =>
  graneroInstance
    .post('/ogc/my/collections/validate', feature)
    .then((res: AxiosResponse) => res.data);

const getAlternates = (feature: Feature) =>
  graneroInstance
    .post('/ogc/my/collections/alternates', feature)
    .then((res: AxiosResponse) => res.data);

const getPropertyKeys = (collectionId: string) =>
  getCollectionPropertyNames(collectionId, {dataset: 'my'})?.properties ?? [];

// -------------------------
// Hooks

export const useGetInfiniteCollections = (params = {}, queryOptions = {}) => {
  return useInfiniteQuery({
    queryKey: ['infiniteCollections', params],
    queryFn: ({pageParam: nextPageUrl}) =>
      nextPageUrl
        ? graneroInstance
            .get(nextPageUrl)
            .then((res: AxiosResponse<FeatureCollection>) => res.data)
        : getCollections(params),
    getNextPageParam: lastPage =>
      (lastPage?.links ?? []).find((link: Link) => link.rel === 'next')?.href,
    initialPageParam: '',
    refetchInterval: DURATION_MS.FIVE_MINUTES,
    staleTime: DURATION_MS.FIVE_MINUTES,
    select: data => {
      return {
        ...data,
        collections: data.pages.map(page => page.collections).flat(),
        numberMatched: data.pages[0].numberMatched,
      };
    },
    ...queryOptions,
  });
};

export const useGetFeatureCollections = (params = {}, queryOptions = {}) =>
  useQuery({
    queryKey: queryKeys.list(params),
    queryFn: () => getCollections({params}),
    ...queryOptions,
  });

type UseGetFeatureCollectionParams = {
  collectionId?: FeatureCollectionId;
  dataset?: string;
};

export const useGetFeatureCollection = (
  {collectionId, dataset = DATASETS.MY}: UseGetFeatureCollectionParams,
  queryOptions = {},
) => {
  const params: typeof getCollections = {};
  if (dataset === DATASETS.MY) {
    params['_x-lock'] = true;
  }

  return useQuery({
    queryKey: queryKeys.detail({collectionId, dataset, ...params}),
    queryFn: () => getCollection(collectionId, {dataset, params}),
    ...queryOptions,
  });
};

export const useValidateFeature = (feature: Feature, queryOptions = {}) => {
  return useQuery({
    queryKey: [
      'featureCollections',
      'validate',
      hashStringSHA256(JSON.stringify(feature)), // use a SHA instead of a chunky GeoJSON as key
    ],
    queryFn: () => validateFeature(feature),
    ...queryOptions,
  });
};

export const useGetAlternates = (feature: Feature, queryOptions = {}) =>
  useQuery({
    queryKey: [
      'featureCollections',
      'alternates',
      hashStringSHA256(JSON.stringify(feature)),
    ],
    queryFn: () => getAlternates(feature),
    ...queryOptions,
  });

export const useGetFeaturesPropertyKeys = (
  collectionId: string,
  queryOptions = {},
) =>
  useQuery({
    queryKey: ['featureCollections', 'properties', collectionId],
    queryFn: () => getPropertyKeys(collectionId),
    ...queryOptions,
  });

export const useGetCollectionPropertyNames = (
  {collectionId}: {collectionId?: string} = {},
  queryOptions = {},
) =>
  useQuery({
    queryKey: [...queryKeys.all, 'propertyNames', collectionId],
    queryFn: () => getCollectionPropertyNames(collectionId),
    ...queryOptions,
  });

type DeleteCollectionParams = {
  collectionId: string;
};

export const useDeleteCollection = (
  queryOptions: UseMutationOptions<
    unknown,
    void,
    DeleteCollectionParams,
    unknown
  > = {},
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({collectionId}) => deleteCollection(collectionId),
    ...queryOptions,
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({queryKey: queryKeys.lists()});

      if (queryOptions.onSuccess) {
        queryOptions.onSuccess(data, variables, onMutateResult, context);
      }
    },
  });
};

type ShareCollectionParams = {
  collectionId: string;
  share: boolean;
};

export const useShareCollection = (
  queryOptions: UseMutationOptions<
    unknown,
    void,
    ShareCollectionParams,
    unknown
  > = {},
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({collectionId, share}) =>
      share ? shareCollection(collectionId) : unshareCollection(collectionId),
    ...queryOptions,
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.detail(getHashFromId(variables.collectionId)),
      });

      if (queryOptions.onSuccess) {
        queryOptions.onSuccess(data, variables, onMutateResult, context);
      }
    },
  });
};

type UpdateCollectionParams = {
  collectionId: string;
  data: Partial<FeatureCollection>;
};

export const useUpdateCollection = (
  queryOptions: UseMutationOptions<
    unknown,
    void,
    UpdateCollectionParams,
    unknown
  > = {},
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({collectionId, data}) => updateCollection(collectionId, data),
    ...queryOptions,
    onSuccess: async (data, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({queryKey: queryKeys.lists()});

      await queryClient.setQueryData(
        queryKeys.detail(getHashFromId(variables.collectionId)),
        () => data,
      );

      if (queryOptions.onSuccess) {
        queryOptions.onSuccess(data, variables, onMutateResult, context);
      }
    },
  });
};
